import { useTheme } from "@emotion/react";
import { Close, ExpandLess, ExpandMore, MoreVert } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Slide,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { forwardRef, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";

const UpdateConsumableDialog = ({
  open,
  onClose,
  onUpdated,
  onDeleted,
  consumable,
}) => {
  const theme = useTheme();
  const { authToken, ezorder } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const updateConsumable = async (description, uom, retailPrice) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.put(
        `/consumables/${consumable.id}`,
        {
          description,
          uom,
          retailPrice,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        onUpdated();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteConsumable = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await ezorder.delete(`/consumables/${consumable.id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status == 200 || response.status === 201) {
        onDeleted();
      } else {
        if (
          response &&
          response.response &&
          response.response.data &&
          response.response.data.error
        ) {
          setErrorMessage(response.response.data.error);
        } else {
          setErrorMessage("Something went wrong");
        }
      }
    } catch (error) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data?.error) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage("Internal server error");
        }
      } else {
        setErrorMessage("Internal server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const formValidationSchema = Yup.object({
    description: Yup.string().required("description required"),
    uom: Yup.string().required("Unit of Measure Requried"),
    retailPrice: Yup.number()
      .positive("Please enter positive number")
      .required("Retail Price Required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: consumable ? consumable.description : "",
      uom: consumable ? consumable.uom : "",
      retailPrice: consumable ? consumable.retailPrice : 0,
    },
    validationSchema: formValidationSchema,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: (values) => {
      updateConsumable(values.description, values.uom, values.retailPrice);
    },
  });

  if (consumable)
    return (
      <Dialog
        fullScreen={false}
        fullWidth={true}
        open={open}
        onClose={() => {
          if (!loading) handleClose(); // do not allow close when loading
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>Update Consumable #{consumable.id}</Box>
          <IconButton
            onClick={(event) => {
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreVert />
          </IconButton>
          <Menu open={menuOpen} onClose={handleMenuClose} anchorEl={anchorEl}>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                deleteConsumable();
              }}
              disableRipple
            >
              <Close />
              Delete
            </MenuItem>
          </Menu>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            sx={{ marginTop: "8px" }}
            label="Description"
            id="description"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={
              formik.errors.description && `${formik.errors.description}`
            }
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            fullWidth
            sx={{ marginTop: "8px" }}
            label="Unit of Measure"
            id="uom"
            name="uom"
            value={formik.values.uom}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.uom && Boolean(formik.errors.uom)}
            helperText={formik.errors.uom && `${formik.errors.uom}`}
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <TextField
            size="small"
            fullWidth
            sx={{ marginTop: "8px" }}
            label="Retail Price"
            id="retailPrice"
            name="retailPrice"
            value={formik.values.retailPrice}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.retailPrice && Boolean(formik.errors.retailPrice)
            }
            helperText={
              formik.errors.retailPrice && `${formik.errors.retailPrice}`
            }
            onFocus={(e) => {
              e.target.select();
            }}
          />
          <Box>
            {errorMessage && (
              <Box>
                <Typography color={"red"}>{errorMessage}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={loading}
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            loading={loading}
            onClick={() => {
              formik.submitForm();
            }}
          >
            Update
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
};

export default UpdateConsumableDialog;
